import { Injectable, signal } from '@angular/core';

/**
 * Open state for the command palette and the keyboard-shortcuts sheet,
 * shared by the shell (which renders both) and the top bar's search button.
 * Only one of the two is open at a time.
 */
@Injectable({
  providedIn: 'root',
})
export class CommandPaletteService {
  readonly paletteOpen = signal(false);
  readonly shortcutsOpen = signal(false);

  /** Where focus returns when a sheet opened from the palette closes. */
  private handoff: HTMLElement | null = null;

  openPalette() {
    this.shortcutsOpen.set(false);
    this.paletteOpen.set(true);
  }

  closePalette() {
    this.paletteOpen.set(false);
  }

  togglePalette() {
    if (this.paletteOpen()) this.closePalette();
    else this.openPalette();
  }

  /**
   * `returnFocus` is passed by the palette: its input, focused when the sheet
   * opens, is removed in the same pass, so the sheet cannot return focus to it.
   */
  openShortcuts(returnFocus: HTMLElement | null = null) {
    this.handoff = returnFocus;
    this.paletteOpen.set(false);
    this.shortcutsOpen.set(true);
  }

  /** The focus target handed over by the palette, read once. */
  takeReturnFocus(): HTMLElement | null {
    const target = this.handoff;
    this.handoff = null;
    return target;
  }

  closeShortcuts() {
    this.shortcutsOpen.set(false);
  }
}

/** True on Apple platforms, where the palette key is ⌘ rather than Ctrl. */
export function isApplePlatform(): boolean {
  // Browser only: Node 21+ has a navigator too, describing the server.
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const data = (navigator as Navigator & { userAgentData?: { platform?: string } })
    .userAgentData;
  const platform = data?.platform || navigator.platform || navigator.userAgent || '';
  return /mac|iphone|ipad|ipod/i.test(platform);
}

/** Keys typed into a field belong to the field, not to global shortcuts. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  if (el.isContentEditable) return true;
  return !!el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]');
}

/**
 * Closes a dialog only for a click that started and ended on its backdrop,
 * so dragging a text selection out of the dialog does not dismiss it.
 */
export class BackdropDismiss {
  private pressedOnBackdrop = false;

  press(event: Event) {
    this.pressedOnBackdrop = event.target === event.currentTarget;
  }

  shouldClose(event: Event): boolean {
    const close = this.pressedOnBackdrop && event.target === event.currentTarget;
    this.pressedOnBackdrop = false;
    return close;
  }
}

/** Keep Tab and Shift+Tab inside a dialog. */
export function trapTab(event: KeyboardEvent, root: HTMLElement | null | undefined) {
  if (!root) return;
  const focusable = Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => el.offsetParent !== null || el === document.activeElement);

  if (!focusable.length) {
    event.preventDefault();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement as HTMLElement | null;
  const inside = !!active && root.contains(active);

  if (event.shiftKey && (active === first || !inside)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || !inside)) {
    event.preventDefault();
    first.focus();
  }
}
